import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import _ from 'lodash';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useDebouncedCallback } from 'use-debounce';
import { Error } from '../../components/Error';
import { selectErrors } from '../../modules/global/selectors';
import { updateCustomer } from '../../modules/customer/actions';
import {
	selectCustomerData,
	selectCustomerBillingData,
	selectCustomerContactData,
	selectCustomerDeliveryData,
	selectCustomerProcuraData,
	selectCustomerPhoneDirectoryOwnerData,
	selectUpdateCustomerFinished,
} from '../../modules/customer/selectors'
import { CustomerInfoPageContent } from './CustomerInfoPageContent';
import { CustomerValues } from './CustomerValues';
import { OfferingType } from '../../utils/offeringType';

interface Props {
	errors: any[];
	dispatch: (data: any) => void;
	customerData: any;
	customerContactData: any;
	customerDeliveryData: any;
	customerBillingData: any;
	customerProcuraData: any[];
	phoneDirectoryOwnerData: any;
	customerUpdateFinished: boolean;
	accountType: string;
	offeringType: OfferingType;
}

export const CustomerInfoPageImpl: React.FC<Props> = (props: Props) => {

	const [customerValues, setCustomerValues] = useState<CustomerValues>({  // TODO could we simplify this data structure?
		phoneNumber: { field: 'customer-phone', value: null },
		streetAddress: { field: 'delivery-street-address', value: null },
		postalCode: { field: 'delivery-postal-code', value: null },
		city: { field: 'delivery-city', value: null },
	});
	const [marketingAllowed, setMarketingAllowed] = useState<boolean>(false);
	const [customerDataSubmitted, setCustomerDataSubmitted] = useState<boolean>(false);

	const submitPhoneDirectoryOwnerData = (updates: any[]) => {
		props.dispatch(updateCustomer({
			updates,
		}))
	};
	const [delayedCallback] = useDebouncedCallback(submitPhoneDirectoryOwnerData, 1500);

	useEffect(() => {
		setMarketingAllowed(props.customerData.toJS().marketingAllowed);
	}, [props.customerData]);

	const submitCustomerData = () => {
		const updates = [];
		_.map(customerValues, (update) => {
			if (update.value !== null) {
				updates.push({
					field: update.field,
					value: update.value,
				})
			}
		});
		if (props.customerData.toJS().marketingAllowed !== marketingAllowed) {
			updates.push({
				field: 'marketing-allowed',
				value: marketingAllowed,
			})
		}
		if (updates.length > 0) {
			props.dispatch(updateCustomer({
				updates,
			}));
			setCustomerValues({
				phoneNumber: { field: 'customer-phone', value: null },
				streetAddress: { field: 'delivery-street-address', value: null },
				postalCode: { field: 'delivery-postal-code', value: null },
				city: { field: 'delivery-city', value: null }
			});
			setCustomerDataSubmitted(true);
			document.body.scrollTop = document.documentElement.scrollTop = 0;
		}
	};

	const handleCustomerDataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const update = (customerValues as any)[name];
		setCustomerValues({
			...customerValues,
			[name]: { ...update, value },
		})
	};

	const phoneDirectoryOwnerDataChangeHandler = (field: string, value: string) => {
		const updates = [
			{
				field,
				value,
			},
		];
		delayedCallback(updates)
	};

	const handleMarketingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === 'marketingAllowed') {
			setMarketingAllowed(true);
		} else {
			setMarketingAllowed(false);
		}
	};

	const { customerContactData, customerDeliveryData, customerBillingData, customerProcuraData, phoneDirectoryOwnerData, errors } = props;
	const ready = customerContactData !== null && phoneDirectoryOwnerData !== null && customerDeliveryData && customerBillingData !== null && customerProcuraData !== null;
	const noErrors = errors.length < 1;
	return (
		<article>
			<Helmet
				title="Asiakastiedot"
				meta={[
					{ name: 'description', content: 'Asiakastiedot' },
				]}
			/>
			{ready && noErrors &&
				<CustomerInfoPageContent
					accountType={props.accountType}
					customerDataSubmitted={customerDataSubmitted}
					customerUpdateFinished={props.customerUpdateFinished}
					customerValues={customerValues}
					customerContactData={props.customerContactData}
					customerDeliveryData={props.customerDeliveryData}
					marketingAllowed={marketingAllowed}
					phoneDirectoryOwnerData={props.phoneDirectoryOwnerData}
					customerBillingData={props.customerBillingData}
					customerProcuraData={props.customerProcuraData}
					handleCustomerDataChange={handleCustomerDataChange}
					handleMarketingChange={handleMarketingChange}
					phoneDirectoryOwnerDataChangeHandler={phoneDirectoryOwnerDataChangeHandler}
					submitCustomerData={submitCustomerData}
				/>
			}
			{!noErrors && (
				<Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />
			)}
		</article>
	);
};

const mapStateToProps = createStructuredSelector({
	errors: selectErrors,
	customerData: selectCustomerData,
	customerContactData: selectCustomerContactData,
	customerDeliveryData: selectCustomerDeliveryData,
	customerBillingData: selectCustomerBillingData,
	customerProcuraData: selectCustomerProcuraData,
	phoneDirectoryOwnerData: selectCustomerPhoneDirectoryOwnerData,
	customerUpdateFinished: selectUpdateCustomerFinished,
});

export default connect(mapStateToProps)(CustomerInfoPageImpl);

